// What a workflow needs: its attached skills and its requirements (credentials, OAuth, VM).
import type { ReactNode } from "react";

import type { CredentialChoice, Toolchain } from "../../../../catalog/types";
import { plural, rich } from "../../../helpers";

const p = plural("en");

export default {
  skills: {
    unknown: "Unknown skill",
    archived: "Archived",
    files: (n: number) => p(n, { one: "# file", other: "# files" }),
    row: (n: number) => `Skill ${n}`,
    moveUp: (name: string) => `Move ${name} up`,
    moveDown: (name: string) => `Move ${name} down`,
    detach: (name: string) => `Detach ${name}`,
    loading: "Loading the skills library…",
    loadError: "Could not load the skills",
    attached: "Attached skills",
    attachedHelp: (path: ReactNode) => rich("Written to ", path, " before every step, so Claude Code finds them. Name the skill a step should use in its prompt."),
    noneAttached: "No skills attached.",
    attachTitle: "Attach a skill",
    allAttached: (manage: ReactNode) => rich("Every active skill is attached. ", manage),
    manage: "Manage skills",
    skill: "Skill",
    attach: "Attach",
    freezeHint: "Publishing freezes a copy of each attached skill; edit a skill, then publish again to use the change.",
  },
  requirements: {
    credential: {
      anthropic: "Claude model key (API key or subscription token)",
      anthropic_api_key: "Anthropic API key",
      anthropic_oauth_token: "Claude subscription token",
      github_pat: "GitHub token",
      github_app: "GitHub App",
      jira_token: "Jira token",
      slack_bot: "Slack bot token",
      db: "Database login",
      aws_sso: "AWS SSO",
      vpn_profile: "VPN profile",
      generic: "Generic secret",
      cursor_api_key: "Cursor API key (coming soon)",
    } satisfies Record<CredentialChoice, string>,
    optional: (label: string) => `${label} (optional)`,
    oauth: (provider: string) => `OAuth: ${provider}`,
    dedicatedVm: "Dedicated VM",
    credentialRow: (n: number) => `Credential ${n}`,
    oauthRow: (n: number) => `OAuth provider ${n}`,
    removeCredential: (n: number) => `Remove credential ${n}`,
    removeOauth: (n: number) => `Remove oauth provider ${n}`,
    credentialField: "Credential",
    name: "Name for scripts",
    namePlaceholder: "jira",
    nameHelp: "Optional; lower case. A pre-execution script's broker call names it.",
    required: "Required",
    platformFallback: "Platform key when the user binds none",
    oauthProvider: "OAuth provider",
    enforcedLater: "Enforced later",
    section: "Credentials and accounts",
    sectionHelp: "When a user creates a task group they bind each credential to one of their secrets. A group cannot start while a required one is missing.",
    noCredentials: "This workflow needs no credentials.",
    addCredential: "Credential",
    addOauth: "OAuth provider",
    isolation: "Isolation",
    vmTitle: "VM isolation",
    vmHelp: "Each task group gets its own Incus virtual machine, reused for its tasks and destroyed an hour after the last one.",
    vmSwitch: "Run in a dedicated VM",
    toolchainsTitle: "Toolchains",
    toolchainsHelp: "Installed in the group's own worker image, so every step has them. A group cannot start a task until its owner adds the ones required here.",
    toolchain: (name: string) => `Toolchain: ${name}`,
    toolchainNames: { rust: "Rust (cargo)", go: "Go", java: "Java (JDK 17, Maven)", bazel: "Bazel (Bazelisk)" } satisfies Record<Toolchain, string>,
  },
};
